import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function BristolPage() {
  return (
    <>
      <LocalBusinessSchema city="Bristol" latitude={51.4545} longitude={-2.5879} />
      <PageHero
        title="Fractional CISO Services in Bristol"
        subtitle="Cybersecurity leadership for the South West's innovation powerhouse"
        eyebrow="Bristol"
        seoTitle="Fractional CISO Bristol | The CISO Network"
        seoDescription="Expert fractional CISO services for Bristol businesses. Access senior cybersecurity leadership for your South West tech company."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing Bristol's Thriving Tech Ecosystem
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Bristol has emerged as one of the UK's most dynamic tech hubs, with a particular
                  strength in aerospace, robotics, fintech, and creative technologies. The city's
                  Temple Quarter Enterprise Zone and the burgeoning harbour-side tech cluster are
                  home to hundreds of innovative businesses pushing boundaries in their respective
                  fields.
                </p>
                <p>
                  The South West region presents unique cybersecurity challenges. Aerospace and
                  defence contractors face stringent supply chain security requirements, while
                  fintech startups must navigate complex regulatory landscapes. Meanwhile, the
                  region's creative and media tech companies handle valuable intellectual property
                  that requires robust protection.
                </p>
                <p>
                  Despite this thriving ecosystem, many Bristol businesses struggle to access senior
                  cybersecurity talent. The city's competitive job market and the high cost of
                  full-time executive hires leave growing companies exposed. A fractional CISO
                  provides the strategic leadership these businesses need without the overhead.
                </p>
                <p>
                  Bristol's key industries - aerospace, fintech, robotics, and creative technologies
                  - each carry distinct security requirements. Aerospace suppliers must meet
                  stringent defence customer standards, fintechs face FCA and PCI-DSS obligations,
                  and creative firms protect valuable IP. A fractional CISO with sector-specific
                  experience can navigate these overlapping demands efficiently.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your dedicated account manager understands the unique needs of South West
                      businesses, from aerospace supply chains to creative tech startups.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Local Presence
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your fractional CISO can meet you at your Bristol offices or collaborate
                      remotely, with the flexibility to be on-site when needed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Bristol Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Enterprise-grade security leadership accessible to South West businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Supply Chain Security',
                description:
                  'Meet the stringent security requirements of aerospace and defence contractors.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description:
                  'Access C-level expertise without the £120k+ salary of a full-time Bristol CISO.',
              },
              {
                icon: Users,
                title: 'Sector Expertise',
                description:
                  'CISOs with experience in aerospace, fintech, robotics, and creative tech.',
              },
              {
                icon: Clock,
                title: 'Flexible Engagement',
                description: 'Scale support up or down based on project needs, funding, or audits.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Bristol - FAQs"
        subtitle="Common questions from Bristol and South West businesses"
        items={[
          {
            id: 'bristol-1',
            question: 'How does a fractional CISO work with businesses in Bristol?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Bristol. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing aerospace, fintech, robotics, and creative technology companies.',
          },
          {
            id: 'bristol-2',
            question: 'What size of business benefits from a fractional CISO in Bristol?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Bristol startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'bristol-3',
            question: 'What industries do your CISOs cover in Bristol?',
            answer:
              "Our network includes CISOs with experience in aerospace, fintech, robotics, and creative technology. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'bristol-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'bristol-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Bristol Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your
            innovation.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
